import React, { useMemo, useState } from 'react'
import { View, TextInput, TouchableOpacity } from 'react-native'
import { Text, Inline, Stack, Button } from '../../../../components/DesignSystem'
import { formStyle, mediumTextSize } from '../../../../constants/GlobalStyle'
import { getGiftCardByCode } from '../../../../firebase/fbDataRetrieval/getGiftCard'
import { isGiftCardValid } from '../../Helpers/validateGiftCard'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { setCheckoutState } from '../../../../state/Slices/checkoutSlice'
import Colors from '../../../../constants/Colors'
import { Feather } from '@expo/vector-icons'
import {
	APPLY_GIFT_CARD,
	GIFT_CARD_ADDED,
	GIFT_CARD_CODE_PLACEHOLDER,
	GIFT_CARD_ERRORS,
	GIFT_CARD_INPUT_MAX_LENGTH,
	GIFT_CARD_LENGTH,
	GIFT_CARD_PROMPT_TEXT,
	GIFT_CARD_REDEEM_TEXT,
	GIFT_CARD_REMOVED,
} from '../../../../constants/checkout/GiftCards'
import Formatter from '../../../../helpers/Formatter'

interface ApplyGiftCardFormProps {
	totalAmount: number
}

export default function ApplyGiftCardForm({ totalAmount }: ApplyGiftCardFormProps) {
	const dispatch = useAppDispatch()
	const { deviceHasInternet } = useAppSelector(state => state.user)
	const { giftCard } = useAppSelector(state => state.checkout)

	const [code, setCode] = useState<string>('')
	const [error, setError] = useState<string>('')
	const [statusText, setStatusText] = useState<string>('')
	const [showInput, setShowInput] = useState<boolean>(false)

	// The 16 char gift card code. Refactor similar logic in ExistingGiftCardMenu.tsx.
	const cleanedCode = code.replaceAll(' ', '')
	const isValidCode = cleanedCode.length === GIFT_CARD_LENGTH

	const formattedCode = useMemo(() => {
		if (!code) return ''
		return Formatter.formatGiftCardCode(code)
	}, [code])

	const toggleInput = () => {
		setShowInput(!showInput)
	}

	const handleSubmit = async () => {
		if (cleanedCode.length != GIFT_CARD_LENGTH) return setError(GIFT_CARD_ERRORS.invalidCode)

		const giftCard = await getGiftCardByCode(cleanedCode)
		if (!giftCard) return setError(GIFT_CARD_ERRORS.doesNotExist)

		if (!isGiftCardValid(giftCard)) return setError(GIFT_CARD_ERRORS.notAllowed)

		setStatusText(GIFT_CARD_ADDED)
		dispatch(setCheckoutState({ giftCard }))
	}

	const removeGiftCard = () => {
		setStatusText(GIFT_CARD_REMOVED)
		dispatch(setCheckoutState({ giftCard: null }))
	}

	return (
		<Stack gap="lg">
			<Text weight="semibold" tone="tertiary" level="large">
				{GIFT_CARD_PROMPT_TEXT}
			</Text>
			{giftCard ? (
				<>
					<View
						style={{
							display: 'flex',
							flexDirection: 'row',
							gap: 8,
							alignItems: 'flex-start',
							justifyContent: 'center',
						}}
					>
						<Text style={{ textAlign: 'left', marginVertical: 10 }}>**** {code.slice(12)}</Text>
						<TouchableOpacity onPress={removeGiftCard} style={{ width: 30 }}>
							<Feather name="x" size={24} color="black" />
						</TouchableOpacity>
					</View>
					{statusText && (
						<Text
							style={{
								textAlign: 'left',
								color: statusText.endsWith('was applied to this order.') ? 'green' : 'red',
								fontSize: mediumTextSize,
							}}
						>
							{statusText}
						</Text>
					)}
				</>
			) : (
				<>
					<Inline gap="sm">
						<Button
							onPress={toggleInput}
							variant="ghost"
							size="medium"
							weight="regular"
							type="link"
						>
							{GIFT_CARD_REDEEM_TEXT}
						</Button>
					</Inline>
					{showInput && (
						<Stack gap="sm">
							<Inline gap="sm">
								<TextInput
									returnKeyType={'done'}
									style={[
										formStyle.textInput,
										{
											flex: 1,
											fontSize: mediumTextSize,
										},
									]}
									placeholder={GIFT_CARD_CODE_PLACEHOLDER}
									placeholderTextColor="#aaa"
									value={formattedCode}
									maxLength={GIFT_CARD_INPUT_MAX_LENGTH}
									onChangeText={setCode}
								/>
								<Button
									onPress={handleSubmit}
									disabled={!deviceHasInternet || !isValidCode}
									backgroundColor={
										!deviceHasInternet || !isValidCode ? Colors.greyscale[6] : global.orgColor
									}
									size="medium"
									variant="primary"
									weight="regular"
								>
									{APPLY_GIFT_CARD}
								</Button>
							</Inline>
							<Text tone="secondary" level="small">
								Gift card code is case-sensitive.
							</Text>
							{error && (
								<Text tone="critical" level="small">
									{error}
								</Text>
							)}
						</Stack>
					)}
				</>
			)}
		</Stack>
	)
}
